/**
 * The preview / progress table inside "Add several": one line per pasted
 * address with its resolved occupation and either the reason it will be
 * skipped or, once the run starts, what happened to it. Presentation only.
 */
import type { BulkRow, RowOutcome, RowProblem } from '@/lib/bulkAddUsers';

const PROBLEM_LABEL: Record<RowProblem, string> = {
  'invalid-email': 'Invalid email',
  duplicate: 'Duplicate',
  'already-on-site': 'Already on site',
  'unknown-occupation': 'Unknown occupation',
  'missing-occupation': 'No occupation',
};

const OUTCOME_LABEL: Record<RowOutcome['kind'], { text: string; pill: string }> = {
  invited: { text: 'Invited', pill: 'good' },
  added: { text: 'Added', pill: 'good' },
  'invite-failed': { text: 'Added, invite failed', pill: 'warn' },
  failed: { text: 'Failed', pill: 'bad' },
};

export function BulkAddPreviewTable({
  rows,
  outcomes,
  activeKey,
}: {
  rows: BulkRow[];
  outcomes: ReadonlyMap<string, RowOutcome>;
  /** Row currently being written, if a run is in progress. */
  activeKey: string | null;
}) {
  if (rows.length === 0) return null;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
      <table className="tbl" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ width: 28 }}>#</th>
            <th>Email</th>
            <th>Occupation</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const outcome = outcomes.get(r.key);
            const running = r.key === activeKey;
            return (
              <tr key={r.key} style={{ opacity: r.problem && r.problem !== 'already-on-site' ? 0.85 : 1 }}>
                <td style={{ color: 'var(--muted)' }}>{r.line}</td>
                <td className="mono" style={{ wordBreak: 'break-all' }}>
                  {r.email || <span style={{ color: 'var(--muted)' }}>{r.raw}</span>}
                </td>
                <td>{r.problem === 'invalid-email' ? '' : r.occupation || '—'}</td>
                <td>
                  <RowStatus row={r} outcome={outcome} running={running} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RowStatus({
  row,
  outcome,
  running,
}: {
  row: BulkRow;
  outcome: RowOutcome | undefined;
  running: boolean;
}) {
  if (running) {
    return <span className="pill plain info">Adding…</span>;
  }
  if (outcome) {
    const { text, pill } = OUTCOME_LABEL[outcome.kind];
    return (
      <span title={outcome.note} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <span className={`pill plain ${pill}`}>{text}</span>
        {outcome.kind !== 'invited' && outcome.note ? (
          <span style={{ color: 'var(--muted)', fontSize: 11 }}>{clip(outcome.note, 60)}</span>
        ) : null}
      </span>
    );
  }
  if (row.problem) {
    const skipOnly = row.problem === 'already-on-site' || row.problem === 'duplicate';
    return (
      <span title={row.note} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <span className={`pill plain ${skipOnly ? 'neutral' : 'warn'}`}>{PROBLEM_LABEL[row.problem]}</span>
        <span style={{ color: 'var(--muted)', fontSize: 11 }}>{clip(row.note, 60)}</span>
      </span>
    );
  }
  return <span className="pill plain neutral">Ready</span>;
}

function clip(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
